import { DialogTitle } from "@headlessui/react";

import { clsx } from "clsx";

import { useId, type ReactNode } from "react";
import { PrimaryButton } from "~/components/Button";
import { Text } from "~/components/Text";
import { CheckoutFooter } from "./CheckoutFooter";

type CheckoutQuestionStepProps = {
  title: string;
  description?: string;
  compact?: boolean;
  children: ReactNode;
  onSubmit: () => void | Promise<void>;
  onBack?: () => void;
  backDisabled?: boolean;
  loading?: boolean;
  submitDisabled?: boolean;
  submitLabel?: string;


  status?: ReactNode;
  error?: string | null;
};

export function CheckoutQuestionStep({
  title,
  description,
  compact = false,
  children,
  onSubmit,
  onBack,
  backDisabled = false,
  loading = false,
  submitDisabled = false,
  submitLabel = "Next",


  status,
  error,
}: CheckoutQuestionStepProps) {
  return (
    <div className={clsx("flex min-h-0 flex-1 flex-col", compact ? "mt-0" : "mt-[10px]")}>
      <form
        onSubmit={(event) => {
          event.preventDefault();
          void onSubmit();
        }}
        className="flex min-h-0 flex-1 flex-col"
      >
        <div className={clsx("-mx-6 min-h-0 flex-1 overflow-y-auto overscroll-contain px-6 pb-4 sm:mx-0 sm:overflow-visible sm:px-0 sm:pb-0", compact ? "pt-0" : "pt-[30px]")}>
          {status && <div className="mb-4">{status}</div>}
          <Text as={DialogTitle} variant="primary">
            {title}
          </Text>
          {description && <Text
            as="p"
            variant="secondary"
            size="sm"
            className="mt-2 max-w-md leading-relaxed"
          >
            {description}
          </Text>}
          <div className="mt-5">{children}</div>
          {error && (
            <Text role="alert" size="sm" color="pink" className="mt-4">
              {error}
            </Text>
          )}
        </div>

        <CheckoutFooter onBack={onBack} backDisabled={loading || backDisabled} showTerms={false}>
          <PrimaryButton
            type="submit"
            loading={loading}
            disabled={loading || submitDisabled}
            className="text-base!"
          >
            {submitLabel}
          </PrimaryButton>
        </CheckoutFooter>
      </form>
    </div>
  );
}

type CheckoutTextFieldProps = Omit<
  React.ComponentPropsWithoutRef<"input">,
  "className" | "id"
> & {
  label: string;
  error?: string;
};

export function CheckoutTextField({
  label,
  error,
  type = "text",
  ...props
}: CheckoutTextFieldProps) {
  const id = useId();
  const messageId = useId();

  return (
    <div>
      <label htmlFor={id} className="sr-only">
        {label}
      </label>
      <input
        id={id}
        type={type}
        aria-invalid={error ? true : undefined}
        aria-describedby={error ? messageId : undefined}
        className={clsx(
          "block h-14 w-full rounded-[9px] border bg-white px-3 font-sans text-base text-butter-green outline-none transition-colors",
          "placeholder:font-normal placeholder:text-placeholder-text",
          error
            ? "border-butter-pink focus:border-butter-pink"
            : "border-butter-cream focus:border-butter-green/60",
        )}
        {...props}
      />
      {error && (
        <Text id={messageId} role="alert" size="sm" color="pink" className="mt-2">
          {error}
        </Text>
      )}
    </div>
  );
}
